import { useState, useCallback, useMemo } from "react";
import type {
  UseExpandableTableState,
  UseExpandableTableOptions,
  LoadingRowsState,
  RowDetailsState,
} from "../types";

/**
 * Hook to manage expandable table state including:
 * - Expanded rows tracking
 * - Loading states for lazy-loaded rows
 * - Row details (child data) caching
 *
 * @example
 * ```tsx
 * const {
 *   expandedRows,
 *   setExpandedRows,
 *   loadingRows,
 *   setRowLoading,
 *   rowDetails,
 *   setRowDetails,
 * } = useExpandableTable<Item, number>({
 *   dataKey: "id",
 *   onRowExpand: async (row) => {
 *     setRowLoading(row.id, true);
 *     const children = await fetchChildren(row.id);
 *     setRowDetails(row.id, children);
 *     setRowLoading(row.id, false);
 *   },
 * });
 * ```
 */
export function useExpandableTable<
  T extends Record<string, unknown>,
  K extends string | number = string,
>(options: UseExpandableTableOptions<T>): UseExpandableTableState<T, K> {
  const {
    dataKey,
    initialExpandedRows = {},
    onRowExpand,
    onRowCollapse,
  } = options;

  // Expanded rows state
  const [expandedRows, setExpandedRows] =
    useState<Record<string, boolean>>(initialExpandedRows);

  // Loading state for individual rows
  const [loadingRows, setLoadingRows] = useState<LoadingRowsState<K>>(
    {} as LoadingRowsState<K>,
  );

  // Row details (child data) cache
  const [rowDetails, setRowDetailsState] = useState<RowDetailsState<K>>(
    {} as RowDetailsState<K>,
  );

  // Get the key value from a row
  const getRowKey = useCallback(
    (row: T): string => {
      const key = row[dataKey as keyof T];
      return String(key);
    },
    [dataKey],
  );

  // Toggle a row's expansion state
  const toggleRow = useCallback(
    (row: T) => {
      const key = getRowKey(row);
      setExpandedRows((prev) => {
        const isExpanded = prev[key];
        const next = { ...prev };

        if (isExpanded) {
          delete next[key];
          onRowCollapse?.(row);
        } else {
          next[key] = true;
          onRowExpand?.(row);
        }

        return next;
      });
    },
    [getRowKey, onRowExpand, onRowCollapse],
  );

  // Expand a specific row
  const expandRow = useCallback(
    (row: T) => {
      const key = getRowKey(row);
      setExpandedRows((prev) => {
        if (prev[key]) return prev;
        onRowExpand?.(row);
        return { ...prev, [key]: true };
      });
    },
    [getRowKey, onRowExpand],
  );

  // Collapse a specific row
  const collapseRow = useCallback(
    (row: T) => {
      const key = getRowKey(row);
      setExpandedRows((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        onRowCollapse?.(row);
        return next;
      });
    },
    [getRowKey, onRowCollapse],
  );

  // Collapse all rows
  const collapseAll = useCallback(() => {
    setExpandedRows({});
  }, []);

  // Expand all rows
  const expandAll = useCallback(
    (rows: T[]) => {
      const expanded: Record<string, boolean> = {};
      rows.forEach((row) => {
        expanded[getRowKey(row)] = true;
      });
      setExpandedRows(expanded);
    },
    [getRowKey],
  );

  // Set loading state for a specific row
  const setRowLoading = useCallback((key: K, loading: boolean) => {
    setLoadingRows((prev) => {
      if (loading) {
        return { ...prev, [key]: true };
      } else {
        const next = { ...prev };
        delete next[key];
        return next;
      }
    });
  }, []);

  // Check if a row is loading
  const isRowLoading = useCallback(
    (key: K): boolean => {
      return !!loadingRows[key];
    },
    [loadingRows],
  );

  // Set details for a specific row
  const setRowDetails = useCallback((key: K, details: unknown) => {
    setRowDetailsState((prev) => ({
      ...prev,
      [key]: details,
    }));
  }, []);

  // Get details for a specific row
  const getRowDetails = useCallback(
    <D = unknown>(key: K): D | undefined => {
      return rowDetails[key] as D | undefined;
    },
    [rowDetails],
  );

  // Clear row details
  const clearRowDetails = useCallback((key?: K) => {
    if (key !== undefined) {
      setRowDetailsState((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setRowDetailsState({} as RowDetailsState<K>);
    }
  }, []);

  return useMemo(
    () => ({
      expandedRows,
      setExpandedRows,
      toggleRow,
      expandRow,
      collapseRow,
      collapseAll,
      expandAll,
      loadingRows,
      setRowLoading,
      isRowLoading,
      rowDetails,
      setRowDetails,
      getRowDetails,
      clearRowDetails,
    }),
    [
      expandedRows,
      toggleRow,
      expandRow,
      collapseRow,
      collapseAll,
      expandAll,
      loadingRows,
      setRowLoading,
      isRowLoading,
      rowDetails,
      setRowDetails,
      getRowDetails,
      clearRowDetails,
    ],
  );
}

/**
 * Simplified hook for managing just the loading and details state
 * when you want to manage expanded rows separately.
 *
 * @example
 * ```tsx
 * const { loadingRows, setRowLoading, rowDetails, setRowDetails } =
 *   useRowDetailsState<number>();
 *
 * const handleExpand = async (row: Item) => {
 *   if (rowDetails[row.id]) return; // Already loaded
 *   setRowLoading(row.id, true);
 *   const children = await fetchChildren(row.id);
 *   setRowDetails(row.id, children);
 *   setRowLoading(row.id, false);
 * };
 * ```
 */
export function useRowDetailsState<K extends string | number = string>() {
  const [loadingRows, setLoadingRowsState] = useState<LoadingRowsState<K>>(
    {} as LoadingRowsState<K>,
  );
  const [rowDetails, setRowDetailsState] = useState<RowDetailsState<K>>(
    {} as RowDetailsState<K>,
  );

  const setRowLoading = useCallback((key: K, loading: boolean) => {
    setLoadingRowsState((prev) => {
      if (loading) {
        return { ...prev, [key]: true };
      } else {
        const next = { ...prev };
        delete next[key];
        return next;
      }
    });
  }, []);

  const isRowLoading = useCallback(
    (key: K): boolean => {
      return !!loadingRows[key];
    },
    [loadingRows],
  );

  const setRowDetails = useCallback((key: K, details: unknown) => {
    setRowDetailsState((prev) => ({
      ...prev,
      [key]: details,
    }));
  }, []);

  const getRowDetails = useCallback(
    <D = unknown>(key: K): D | undefined => {
      return rowDetails[key] as D | undefined;
    },
    [rowDetails],
  );

  const clearRowDetails = useCallback((key?: K) => {
    if (key !== undefined) {
      setRowDetailsState((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setRowDetailsState({} as RowDetailsState<K>);
    }
  }, []);

  return useMemo(
    () => ({
      loadingRows,
      setRowLoading,
      isRowLoading,
      rowDetails,
      setRowDetails,
      getRowDetails,
      clearRowDetails,
    }),
    [
      loadingRows,
      setRowLoading,
      isRowLoading,
      rowDetails,
      setRowDetails,
      getRowDetails,
      clearRowDetails,
    ],
  );
}

export default useExpandableTable;
